<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { format } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';

import BillingAPI from 'dashboard/api/billing';
import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import PurchaseCreditsModal from './components/PurchaseCreditsModal.vue';
import PackagePicker from './components/PackagePicker.vue';
import ManualPaymentDialog from './components/ManualPaymentDialog.vue';
import SubmissionHistory from './components/SubmissionHistory.vue';
import PlanSummary from './components/PlanSummary.vue';
import UsageOverview from './components/UsageOverview.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import ButtonV4 from 'next/button/Button.vue';
import { getCurrencyConfig } from 'dashboard/constants/billing';
import { useI18n } from 'vue-i18n';

const { currentAccount, isOnChatwootCloud, accountScopedRoute } = useAccount();
const {
  captainEnabled,
  captainLimits,
  documentLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const PAYMENT_STATUS_KEYS = {
  completed: 'BILLING_SETTINGS.KHALTI.STATUS.COMPLETED',
  pending: 'BILLING_SETTINGS.KHALTI.STATUS.PENDING',
  failed: 'BILLING_SETTINGS.KHALTI.STATUS.FAILED',
};

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

// State for handling refresh attempts and loading
const isWaitingForBilling = ref(false);
const purchaseCreditsModalRef = ref(null);

// Currency selection shown to new accounts whose locale supports a non-USD currency.
const currencySelectionRequired = ref(false);
const currencyOptions = ref([]);

// Nepal manual lane: packages, top-ups and the customer's own submissions.
const packages = ref([]);
const topups = ref([]);
const providers = ref([]);
const manualDetails = ref({});
const submissions = ref([]);
const manualDialogRef = ref(null);
const packagePickerRef = ref(null);

// Any lane a customer can pay through without leaving the product.
const IN_APP_PROVIDERS = ['manual_qr', 'khalti'];

const inAppLaneEnabled = computed(() =>
  providers.value.some(provider => IN_APP_PROVIDERS.includes(provider))
);
const hasPendingSubmission = computed(() =>
  submissions.value.some(submission => submission.status === 'pending')
);

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

// The subscription overview reads the package a customer actually holds, which the
// account payload carries directly. `custom_attributes.plan_name` above is the older
// Stripe-only lane and stays untouched.
const subscribedPlanName = computed(() => currentAccount.value.plan_name);
const planLimits = computed(() => currentAccount.value.plan_limits || {});
const aiCredits = computed(() => currentAccount.value.ai_credits || {});

const canPurchaseCredits = computed(() => {
  const plan = planName.value?.toLowerCase();
  return plan && plan !== 'hacker';
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const billingCurrency = computed(() => {
  if (!customAttributes.value.billing_currency) return '';
  return t(
    getCurrencyConfig(customAttributes.value.billing_currency).i18nLabelKey
  );
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    const data = await store.dispatch('accounts/subscription');
    currencySelectionRequired.value = !!data?.currency_selection_required;
    currencyOptions.value = data?.currency_options || [];
  }
};

const fetchSubmissions = async () => {
  const { data } = await BillingAPI.getSubmissions();
  submissions.value = data.payload;
};

const fetchBillingLane = async () => {
  const { data } = await BillingAPI.getPackages();
  packages.value = data.packages;
  topups.value = data.topups;
  providers.value = data.providers;
  manualDetails.value = data.manual_payment_details;
  await fetchSubmissions();
};

// The plan, its limits and the credit spend ride on the account payload, which is
// otherwise only loaded at boot. Refresh it so the overview is current on arrival and
// after a payment lands.
const refreshSubscription = () =>
  store.dispatch('accounts/get', { silent: true });

// The Captain meters come from the limits endpoint; everything else rides on
// the account payload refreshed by `refreshSubscription`.
const refreshUsage = () => {
  fetchLimits();
  refreshSubscription();
};

const handleBillingPageLogic = async () => {
  refreshUsage();
  await fetchBillingLane();

  // Everything below sets up the Stripe subscription, which is cloud only.
  if (!isOnChatwootCloud.value) return;

  // Check if we've already attempted a refresh for billing setup
  const billingRefreshAttempted = sessionStorage.get(BILLING_REFRESH_ATTEMPTED);

  // If cloud user, fetch account details first
  await fetchAccountDetails();

  // Waiting on the user to pick a billing currency — don't auto-refresh.
  if (currencySelectionRequired.value) return;

  // If still no billing plan after fetch
  if (!hasABillingPlan.value) {
    // If we haven't attempted refresh yet, do it once
    if (!billingRefreshAttempted) {
      isWaitingForBilling.value = true;
      sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);

      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else {
      // We've already tried refreshing, so just show the no billing message
      // Clear the flag for future visits
      sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    }
  } else {
    // Billing plan found, clear any existing refresh flag
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
  }
};

const onSelectCurrency = async code => {
  await store.dispatch('accounts/selectBillingCurrency', code);
  currencySelectionRequired.value = false;
  // Currency stored and customer creation kicked off — resume the standard wait flow.
  await handleBillingPageLogic();
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

// Nepal accounts buy in-app; everywhere else the intent still routes to Stripe or support.
const onClickUpgrade = () => {
  if (inAppLaneEnabled.value) {
    packagePickerRef.value?.scrollIntoView({ behavior: 'smooth' });
  } else if (isOnChatwootCloud.value) {
    onClickBillingPortal();
  } else {
    onToggleChatWindow();
  }
};

const onManageAgents = () => router.push(accountScopedRoute('agent_list'));
const onManageInboxes = () =>
  router.push(accountScopedRoute('settings_inbox_list'));

const onSelectPackage = payload => manualDialogRef.value?.open(payload);

const onPaymentSubmitted = () => {
  fetchSubmissions();
  fetchLimits();
};

const openPurchaseCreditsModal = () => {
  purchaseCreditsModalRef.value?.open();
};

const handleTopupSuccess = () => {
  refreshUsage();
};

// /khalti/callback sends the customer back here with the verdict it already
// verified server-side; the param is consumed so a reload does not re-announce it.
const showPaymentResult = () => {
  const key = PAYMENT_STATUS_KEYS[route.query.payment];
  if (!key) return;

  useAlert(t(key));
  router.replace({ query: { ...route.query, payment: undefined } });
};

onMounted(async () => {
  await handleBillingPageLogic();
  showPaymentResult();
});

const isLoading = computed(
  () => uiFlags.value.isFetchingItem || isWaitingForBilling.value
);

const noRecordsFound = computed(
  () =>
    isOnChatwootCloud.value &&
    !hasABillingPlan.value &&
    !subscribedPlanName.value &&
    !inAppLaneEnabled.value &&
    !isWaitingForBilling.value &&
    !currencySelectionRequired.value
);
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('BILLING_SETTINGS.TITLE')"
    :description="$t('BILLING_SETTINGS.DESCRIPTION')"
  >
    <div
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="
        isWaitingForBilling
          ? $t('BILLING_SETTINGS.NO_BILLING_USER')
          : $t('ATTRIBUTES_MGMT.LOADING')
      "
      class="flex flex-col gap-5"
    >
      <Skeleton
        v-for="row in 4"
        :key="row"
        variant="text"
        :lines="2"
        :announce="false"
      />
    </div>
    <p
      v-else-if="noRecordsFound"
      class="flex items-center justify-center flex-1 py-20 text-base text-n-slate-12"
    >
      {{ $t('BILLING_SETTINGS.NO_BILLING_USER') }}
    </p>
    <template v-else>
      <section v-if="currencySelectionRequired" class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.CURRENCY.SELECT.TITLE')"
          :description="$t('BILLING_SETTINGS.CURRENCY.SELECT.DESCRIPTION')"
        >
          <template #action>
            <div class="flex gap-2">
              <ButtonV4
                v-for="code in currencyOptions"
                :key="code"
                sm
                solid
                blue
                :is-loading="uiFlags.isCheckoutInProcess"
                :disabled="uiFlags.isCheckoutInProcess"
                @click="onSelectCurrency(code)"
              >
                {{ $t(getCurrencyConfig(code).i18nLabelKey) }}
              </ButtonV4>
            </div>
          </template>
        </BillingCard>
      </section>
      <section v-else class="grid gap-4">
        <PlanSummary
          v-if="subscribedPlanName"
          :plan-name="subscribedPlanName"
          :prices="currentAccount.plan_prices"
          :limits="planLimits"
          :features="currentAccount.plan_features"
          :billing-region="currentAccount.billing_region"
          :plan-expires-at="currentAccount.plan_expires_at"
          :trial-ends-at="currentAccount.trial_ends_at"
          @manage="onClickUpgrade"
        />
        <UsageOverview
          :agents-count="currentAccount.agents_count"
          :agents-limit="planLimits.agents"
          :inboxes-count="currentAccount.inboxes_count"
          :inboxes-limit="planLimits.inboxes"
          :credits-consumed="aiCredits.consumed"
          :credits-limit="planLimits.ai_credits"
          @manage-agents="onManageAgents"
          @manage-inboxes="onManageInboxes"
        />
        <template v-if="inAppLaneEnabled">
          <Banner v-if="hasPendingSubmission" color="amber">
            {{ $t('BILLING_SETTINGS.SUBMISSIONS.PENDING_BANNER') }}
          </Banner>
          <div ref="packagePickerRef">
            <PackagePicker
              :packages="packages"
              :topups="topups"
              @select="onSelectPackage"
            />
          </div>
          <SubmissionHistory :submissions="submissions" />
        </template>
        <BillingCard
          v-if="isOnChatwootCloud"
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 sm solid blue @click="onClickBillingPortal">
              {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </template>
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
            <DetailItem
              v-if="billingCurrency"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.CURRENCY')"
              :value="billingCurrency"
            />
          </div>
        </BillingCard>
        <template v-if="isOnChatwootCloud">
          <BillingCard
            v-if="captainEnabled"
            :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
            :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
          >
            <template #action>
              <div class="flex gap-2">
                <ButtonV4
                  sm
                  flushed
                  slate
                  icon="i-lucide-refresh-cw"
                  :is-loading="isFetchingLimits"
                  @click="refreshUsage"
                >
                  {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
                </ButtonV4>
                <ButtonV4
                  v-if="canPurchaseCredits"
                  sm
                  solid
                  blue
                  @click="openPurchaseCreditsModal"
                >
                  {{ $t('BILLING_SETTINGS.TOPUP.BUY_CREDITS') }}
                </ButtonV4>
              </div>
            </template>
            <div v-if="captainLimits && documentLimits" class="px-5">
              <BillingMeter
                :title="$t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS')"
                v-bind="documentLimits"
              />
            </div>
          </BillingCard>
          <BillingCard
            v-else
            :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
            :description="$t('BILLING_SETTINGS.CAPTAIN.UPGRADE')"
          >
            <template #action>
              <ButtonV4 sm solid slate @click="onClickUpgrade">
                {{ $t('CAPTAIN.PAYWALL.UPGRADE_NOW') }}
              </ButtonV4>
            </template>
          </BillingCard>
        </template>

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
      <PurchaseCreditsModal
        ref="purchaseCreditsModalRef"
        @success="handleTopupSuccess"
      />
      <ManualPaymentDialog
        ref="manualDialogRef"
        :manual-details="manualDetails"
        :providers="providers"
        @submitted="onPaymentSubmitted"
      />
    </template>
  </SettingsPageShell>
</template>
